/**
 * A plate's recorded mouths (docs/NEW-UI/09): every unique plate frame × every held sound, as MuseTalk drew it once
 * (avatar/tools/viseme_bake/record.py), and the model that picks one from the audio (tools/mouth-learn.ts).
 *
 *   /static/avatar_plate/<id>/visemes/visemes.json   {avatar_id, frames, unique, zona, sounds, …}
 *   /static/avatar_plate/<id>/visemes/mouths.json    the MouthModel (visemes.ts)
 *   /static/avatar_plate/<id>/visemes/<mouth>/fNNN.jpg  the zone at (zona[0], zona[1]) on unique frame NNN
 *
 * Fetched whole, as JPEG bytes (~2 MB a mouth); decoded when drawn, like the plate's frames (plate.ts).
 */
import { plateFrameIndex, plateFrameUrl, type PlateStore } from './plate';
import { modelOf, type Mouth, type MouthModel } from './visemes';

export interface LibraryMeta {
  avatar_id: string;
  frames: number;
  unique: number;
  zona: [number, number, number, number];
  sounds: string[];
}

export class MouthLibrary {
  private constructor(
    readonly meta: LibraryMeta,
    readonly model: MouthModel,
    private readonly crops: ReadonlyMap<Mouth, ArrayBuffer[]>,
  ) {}

  /** The library beside a loaded plate; refused when it was recorded on another plate. */
  static async load(plate: PlateStore, base = `/static/avatar_plate/${plate.meta.avatar_id}/visemes`): Promise<MouthLibrary> {
    const meta = (await json(`${base}/visemes.json`)) as LibraryMeta;
    if (meta.avatar_id !== plate.meta.avatar_id || meta.unique !== plate.meta.unique) {
      throw new Error(`the mouths were recorded on ${meta.avatar_id} (${meta.unique} frames), not on this plate`);
    }
    const model = modelOf(await json(`${base}/mouths.json`));
    const missing = model.mouths.filter((m) => m !== 'rest' && !meta.sounds.includes(m)); // rest: the plate's own
    if (missing.length) throw new Error(`the model picks mouths the library has not recorded: ${missing.join(' ')}`);
    // one mouth after the other, its frames together: every request at once (15 mouths, 2880) the browser refuses
    // (ERR_INSUFFICIENT_RESOURCES, measured 22/10); a mouth at a time keeps as many open as the plate does
    const crops = new Map<Mouth, ArrayBuffer[]>();
    for (const mouth of meta.sounds) {
      crops.set(mouth as Mouth, await Promise.all(
        Array.from({ length: meta.unique }, async (_, i) => {
          const r = await fetch(plateFrameUrl(`${base}/${mouth}`, i));
          if (!r.ok) throw new Error(`mouth ${mouth} frame ${i}: ${r.status}`);
          return r.arrayBuffer();
        }),
      ));
    }
    return new MouthLibrary(meta, model, crops);
  }

  /** Where a crop goes on the plate frame. */
  get origin(): [number, number] {
    return [this.meta.zona[0], this.meta.zona[1]];
  }

  has(mouth: Mouth): boolean {
    return this.crops.has(mouth);
  }

  /** The crop of `mouth` on plate cycle index `idx` (0..frames-1), as JPEG bytes. */
  crop(mouth: Mouth, idx: number): ArrayBuffer {
    const frames = this.crops.get(mouth);
    if (!frames) throw new Error(`no recorded mouth ${mouth}`);
    return frames[plateFrameIndex(idx, this.meta.frames)]!;
  }
}

async function json(url: string): Promise<unknown> {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`${url}: ${r.status}`);
  return r.json();
}
